
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Spinner } from '@/components/Spinner';

interface RegistrationFormProps {
  name: string;
  setName: (name: string) => void;
  email: string;
  setEmail: (email: string) => void;
  password: string;
  setPassword: (password: string) => void;
  confirmPassword: string;
  setConfirmPassword: (confirmPassword: string) => void;
  acceptTerms: boolean;
  setAcceptTerms: (acceptTerms: boolean) => void;
  acceptPrivacy: boolean;
  setAcceptPrivacy: (acceptPrivacy: boolean) => void;
  validationErrors: {
    password?: string;
    confirmPassword?: string;
  };
  handleSubmit: (e: React.FormEvent) => void;
  isSubmitting: boolean;
}

const RegistrationForm = ({
  name,
  setName,
  email,
  setEmail,
  password,
  setPassword,
  confirmPassword,
  setConfirmPassword,
  acceptTerms,
  setAcceptTerms,
  acceptPrivacy,
  setAcceptPrivacy,
  validationErrors,
  handleSubmit,
  isSubmitting
}: RegistrationFormProps) => {
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="name">Nombre completo</Label>
        <Input
          id="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="password">Contraseña</Label>
        <Input
          id="password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className={validationErrors.password ? "border-red-500" : ""}
          required
        />
        {validationErrors.password && (
          <p className="text-xs text-red-500 mt-1">{validationErrors.password}</p>
        )}
        <p className="text-xs text-gray-500 mt-1">
          La contraseña debe tener al menos 8 caracteres e incluir letras y números.
        </p>
      </div>
      <div className="space-y-2">
        <Label htmlFor="confirmPassword">Confirmar Contraseña</Label>
        <Input
          id="confirmPassword"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          className={validationErrors.confirmPassword ? "border-red-500" : ""}
          required
        />
        {validationErrors.confirmPassword && (
          <p className="text-xs text-red-500 mt-1">{validationErrors.confirmPassword}</p>
        )}
      </div>
      <div className="flex flex-col space-y-3">
        <div className="flex items-start space-x-2">
          <Checkbox 
            id="terms" 
            checked={acceptTerms} 
            onCheckedChange={(checked) => {
              setAcceptTerms(checked as boolean);
            }} 
          />
          <Label htmlFor="terms" className="text-sm font-normal leading-none cursor-pointer">
            He leído y acepto los{' '}
            <Link to="/terminos-condiciones" className="text-vegana-primary hover:underline">
              Términos y Condiciones
            </Link>
          </Label>
        </div>
        <div className="flex items-start space-x-2">
          <Checkbox 
            id="privacy" 
            checked={acceptPrivacy} 
            onCheckedChange={(checked) => {
              setAcceptPrivacy(checked as boolean);
            }} 
          />
          <Label htmlFor="privacy" className="text-sm font-normal leading-none cursor-pointer">
            He leído y acepto la{' '}
            <Link to="/politica-privacidad" className="text-vegana-primary hover:underline">
              Política de Privacidad (RGPD)
            </Link>
          </Label>
        </div>
      </div>
      <Alert className="bg-blue-50 border-blue-200 text-sm">
        <AlertDescription className="text-blue-700">
          Después de registrarte, recibirás un email de verificación. Deberás hacer clic en el enlace para activar tu cuenta.
        </AlertDescription>
      </Alert>
      <Button 
        type="submit" 
        className="w-full bg-vegana-primary hover:bg-vegana-primary/90 mt-4" 
        disabled={isSubmitting}
      >
        {isSubmitting ? (
          <span className="flex items-center gap-2">
            <Spinner size="sm" /> Creando cuenta...
          </span>
        ) : 'Crear cuenta'}
      </Button>
    </form>
  );
};

export default RegistrationForm;
